import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import swal from "sweetalert";
import { useLocation, Link } from 'react-router-dom';
import ConferenceNavbar from "../../../frontend/Conference/ConferenceNavbar";
import NewVersionAbstractFooter from "../../AbstractAuth/NewVersion/NewVersionAnstractFooter";
import {
    Box,
    Button,
    Grid,
    Typography,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import { useDeadline } from "../../../contexts/DeadlineContext";

const ConferencePaymentRetrunRul = () => {
    const { abstractHomeSetup } = useDeadline();
    const [paymentRes, setPaymentRes] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [badgeUrl, setBadgeUrl] = useState(null);
    const [transactionSuccess, setTransactionSuccess] = useState(false);
    const [participntData, setParticipntData] = useState(null);
    const eventid = localStorage.getItem('eventid');
    const emailSentRef = useRef(false);

    const location = useLocation();
    const pathname = location.pathname;
    const segments = pathname.split('/');
    const encrypt_eventId = segments[segments.length - 1];
    const encrypt_participantId = segments[segments.length - 2];

    const primaryColor = abstractHomeSetup.color_code_two || '#05597D';
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

    // Step 1: Call backend to confirm payment
    useEffect(() => {
        const fetchPaymentResponse = async () => {
            try {
                const response = await axios.post(`/api/paytabs/cpayments/callback`, {
                    encrypt_participantId,
                    encrypt_eventId
                });

                const responseData = response.data?.data || response.data;

                if (responseData) {
                    setPaymentRes(responseData);
                    const paytabsData = responseData[0]?.paytabs_data || responseData?.paytabs_data;

                    if (paytabsData?.status === true) {
                        setTransactionSuccess(true);
                    } else {
                        setTransactionSuccess(false);
                    }
                } else {
                    setError("Received empty response from server");
                }
            } catch (error) {
                console.error('API Error:', error);
                setError(error.response?.data?.message || error.message || "Failed to process payment response");
            } finally {
                setLoading(false);
            }
        };

        fetchPaymentResponse();
    }, [encrypt_participantId, encrypt_eventId]);

    // Step 2: Send confirmation email once, and set badge link
    useEffect(() => {
        if (!paymentRes || emailSentRef.current) return;

        const id = paymentRes.p_id;
        const emailStatus = paymentRes.email;

        if (typeof emailStatus === 'undefined') return;

        emailSentRef.current = true;

        const endpoint = emailStatus === 1
            ? `/api/conference-particepant-online-verification-email-send/${id}`
            : `/api/conference-particepant-cancele-online-verification-email-send/${id}`;

        const successMessage = emailStatus === 1
            ? 'Please check your email. Your QR code has been sent to your registered email. Thank you!'
            : 'You canceled your online payment. Thank you!';

        axios.get('/sanctum/csrf-cookie').then(() => {
            axios.post(endpoint, { eventid })
                .then(res => {
                    if (res.data.status === 200) {
                        setBadgeUrl(res.data.badge_link);
                        setParticipntData(res.data.wspfr);
                        swal(emailStatus === 1 ? 'Success' : 'Info', successMessage, emailStatus === 1 ? 'success' : 'info');
                    }
                })
                .catch(error => {
                    console.error('Email Error:', error);
                    swal('Error', 'Failed to send confirmation email', 'error');
                });
        });
    }, [paymentRes, eventid]);

    // Step 3: Retry loading badge after delay if not ready
    useEffect(() => {
        if (!badgeUrl && transactionSuccess) {
            const retryTimer = setTimeout(() => {
                window.location.reload();
            }, 1000); // Retry after 10s

            return () => clearTimeout(retryTimer);
        }
    }, [badgeUrl, transactionSuccess]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <h1>Processing your payment...</h1>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
                <ConferenceNavbar />
                <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', textAlign: 'center', p: 3 }}>
                    <div>
                        <h2>Payment Processing Error</h2>
                        <p>{error}</p>
                        <Button variant="contained" color="primary" component={Link} to="/conference/particpant-verification" sx={{ mt: 2 }}>
                            Back to Verification
                        </Button>
                    </div>
                </Box>
                <NewVersionAbstractFooter />
            </Box>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <Grid item xs={12} sm={10} md={8} lg={6}>
                <ConferenceNavbar />
            </Grid>

            <Box sx={{ m: 2, color: '#000000' }}>
                <Button
                    sx={{ float: 'right', marginTop: '2%', ":hover": { color: "white" } }}
                    color='success'
                    size='small'
                    variant='contained'
                    component={Link}
                    to="/conference/particpant-verification"
                >
                    Back To Verification
                </Button>
            </Box>

            {transactionSuccess ? (
                <Box flexGrow={1} display="flex" flexDirection="column" alignItems="center" justifyContent="center" px={2}>
                    <Typography variant={isSmallScreen ? 'h5' : 'h4'} gutterBottom>
                        🎉 Thank You for Registering!
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 2 }}>
                        Your registration has been successfully completed.
                    </Typography>

                    <Typography variant="body1" sx={{ mt: 1 }}>
                        <strong>QR Code ID:</strong> {paymentRes.p_id}
                    </Typography>

                    {badgeUrl ? (
                        <Button
                            variant="outlined"
                            href={badgeUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                mt: 3,
                                color: primaryColor,
                                borderColor: primaryColor,
                                fontWeight: 'bold',
                                '&:hover': {
                                    backgroundColor: primaryColor,
                                    color: '#fff',
                                    borderColor: '#fff',
                                },
                            }}
                        >
                            View Your Badge
                        </Button>
                    ) : (
                        <Typography variant="body2" color="textSecondary" mt={3}>
                            Loading your badge...
                        </Typography>
                    )}

                    <Typography variant="body1" mt={4} mb={4} textAlign="center">
                        📧 A confirmation email has been sent to your registered email address. If you don't see it in your inbox, please check your spam or junk folder.
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ textAlign: 'center', color: '#000000', mt: '2%', flexGrow: 1, mb: 4 }}>
                    <Typography variant={isSmallScreen ? 'h5' : 'h4'} gutterBottom color="error">
                        ❌ Transaction Not Completed
                    </Typography>

                    <Typography variant="body1" sx={{ mt: 2 }}>
                        You canceled your online payment transaction.
                    </Typography>

                    <Typography variant="body1" sx={{ mt: 2 }}>
                        <strong>Registered ID:</strong> {participntData?.id || paymentRes.p_id}
                    </Typography>

                    <Typography variant="body1" sx={{ mt: 3, color: '#555' }}>
                        Please check your email at <strong>{participntData?.participant_email || paymentRes.email}</strong> for more details.
                    </Typography>

                    <Typography variant="body1" sx={{ mt: 2, fontStyle: 'italic' }}>
                        If you didn't receive the email, please check your spam folder or contact support.
                    </Typography>
                </Box>
            )}

            <Grid item xs={12} sm={10} md={8} lg={6} sx={{ mt: 'auto' }}>
                <NewVersionAbstractFooter />
            </Grid>
        </div>
    );
};

export default ConferencePaymentRetrunRul;
